import SatProductSelect from "./SatProductSelect";
import Spinner from "../../shared/UxbiSpinner";
import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Tab,
  Tabs,
  Card,
} from "react-bootstrap";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import Field from "../../components/common/FormField";
import RemoteSelect from "../../components/common/RemoteSelect";
import { listarCatalogo, obtenerCatalogo } from "../../api/catalogos";
import {
  obtenerProducto,
  crearProducto,
  actualizarProducto,
} from "../../api/productos";
import { extractCollection } from "../../components/documentos/documentoUtils";
import {
  emptyProduct,
  normalizeProduct,
  validateProduct,
  buildProductPayload,
} from "./productoForm";
const labels = (option) =>
  option.nombre || option.descripcion || option.clave || `#${option.id}`;
const catalogs = [
  ["id_prod_tipo", "Tipo de producto", "tipos-producto", "nombre", true],
  ["id_prod_marca", "Marca", "marcas", "nombre", true],
  [
    "id_productos_unidad_medida",
    "Unidad de medida",
    "u-medida",
    "nombre",
    true,
  ],

  ["id_empresa_prod", "Empresa", "empresas", "nombre"],
];
const tabs = {
  generales: [
    ["clave", "Clave", "text", 50],
    ["descripcion", "Descripción", "text", 100],
    ["alias_1", "Alias 1"],
    ["alias_2", "Alias 2"],
    ["talla", "Talla"],
    ["codigo_barras_venta", "Código de barras de venta", "text", 50],
    ["codigo_barras_proveedor", "Código de barras del proveedor", "text", 50],
    ["peso", "Peso (kg)", "number"],

    ["comentarios", "Comentarios", "textarea", 2000],
  ],
  precios: [
    ["precio", "Precio", "number"],
    ["iva", "IVA (%)", "number"],
    ["ieps", "IEPS (%)", "number"],
    ["retenciones_iva", "Retención IVA ventas (%)", "number"],
    ["isr_ventas", "ISR ventas (%)", "number"],
  ],
  costos: [
    ["precio_unitario_factura", "Costo", "number"],
    ["retenciones", "Retención ISR (%)", "number"],
    ["retenciones_iva_compras", "Retención IVA compras (%)", "number"],
  ],
  abasto: [
    ["minimo", "Mínimo", "number"],
    ["maximo", "Máximo", "number"],
    ["punto_reorden", "Punto de reorden", "number"],
    ["multiplo_compra", "Múltiplo de compra", "number"],
    ["dias_entrega", "Días de entrega", "number"],
    ["dias_inventario_meta", "Días de inventario meta", "number"],
    ["alto", "Alto", "number"],
    ["largo", "Largo", "number"],
    ["ancho", "Ancho", "number"],
    ["volumen", "Volumen", "number"],
  ],
};
const flags = {
  generales: [
    ["afecta_inventario", "Afecta inventario"],
    ["disp_ptoventa", "Visible en punto de venta"],
    ["es_paquete", "Es paquete"],
    ["servicio_prorrateo", "Servicio de prorrateo en compras"],
    ["es_producto_terminado", "Producto terminado"],
    ["lista_produccion", "Lista de materiales de producción"],
    ["comercio_electronico", "Comercio electrónico"],
    ["material_peligroso", "Material peligroso"],
  ],
  precios: [
    ["exento_iva", "Exento de IVA"],
  ],
  costos: [],
  abasto: [
    ["estatus", "Activo"],
    ["tipo_stock", "Tipo stock"],
    ["importacion", "Importación"],
    ["tiene_pedimento", "Tiene pedimento"],
    ["tiene_caducidad", "Tiene caducidad"],
  ],
};
export default function ProductoFormModal({
  show,
  productId,
  readOnly,
  onHide,
  onSaved,
}) {
  const [form, setForm] = useState(emptyProduct);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("generales");
  useEffect(() => {
    if (!show) return;
    let active = true;
    setErrors({});
    setLoadError("");
    setLoading(false);
    setActiveTab("generales");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!productId) {
      setForm({ ...emptyProduct(), id_empresa_prod: user.id_empresa || "" });
      listarCatalogo("monedas", {
        limit: 1,
        filters: [{ property: "clave_moneda", operator: "=", value: "MXN" }],
      })
        .then((response) => {
          const currency = extractCollection(response)[0];
          if (active && currency)
            setForm((current) => ({
              ...current,
              id_moneda_compra: current.id_moneda_compra || currency.id,
              id_moneda_venta: current.id_moneda_venta || currency.id,
            }));
        })
        .catch((error) => toast.error(error.message));
    } else {
      setLoading(true);
      obtenerProducto(productId)
        .then((response) => {
          if (active) setForm(normalizeProduct(response));
        })
        .catch((error) => {
          if (active) setLoadError(error.message);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }
    return () => {
      active = false;
    };
  }, [show, productId, revision]);
  const change = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));
  const save = async (event) => {
    event.preventDefault();
    const invalid = validateProduct(form);
    setErrors(invalid);
    if (Object.keys(invalid).length) {
      const first = Object.keys(invalid)[0];
      const target = Object.entries(tabs).find(([, fields]) =>
        fields.some(([name]) => name === first),
      );
      setActiveTab(
        target?.[0] ||
          (first === "id_moneda_compra"
            ? "costos"
            : first === "id_moneda_venta"
              ? "precios"
              : "generales"),
      );
      toast.error("Revisa los campos obligatorios del producto.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildProductPayload(form);
      if (productId) await actualizarProducto(productId, payload);
      else await crearProducto(payload);
      toast.success("Producto guardado correctamente.");
      onSaved();
    } catch (error) {
      setErrors(error.body?.errors || error.body?.errores || {});
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  const remote = (name, label, endpoint, searchProperty, required = false) => (
    <Col md={4} key={name}>
      <RemoteSelect
        {...{ name, label, required }}
        value={form[name]}
        disabled={readOnly || saving || name === "id_empresa_prod"}
        error={errors[name]}
        onChange={change}
        loadOptions={(params) =>
          listarCatalogo(endpoint, { ...params, searchProperty })
        }
        loadById={(id) => obtenerCatalogo(endpoint, id)}
        getOptionLabel={labels}
      />
    </Col>
  );
  const renderField = ([name, label, type = "text", maxLength]) => (
    <Field
      key={name}
      {...{ name, label, type, maxLength }}
      value={form[name]}
      onChange={change}
      error={errors[name]}
      disabled={readOnly || saving}
      md={
        type === "textarea"
          ? 12
          : name === "descripcion"
            ? 8
            : name.startsWith("alias_")
              ? 6
              : 4
      }
      required={["clave", "descripcion"].includes(name)}
      min={type === "number" ? 0 : undefined}
    />
  );
  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      className="catalog-full-modal"
      size="xl"
      scrollable
      backdrop="static"
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          {readOnly
            ? "Detalle del producto"
            : productId
              ? "Editar producto"
              : "Nuevo producto"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={save} noValidate>
        <Modal.Body>
          {loadError ? (
            <div className="alert alert-danger" role="alert">
              {loadError}{" "}
              <Button variant="link" onClick={() => setRevision((v) => v + 1)}>
                Volver a cargar
              </Button>
            </div>
          ) : loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <Tabs
              activeKey={activeTab}
              onSelect={setActiveTab}
              className="mb-4 catalog-form-tabs"
            >
              {Object.entries(tabs).map(([tab, fields]) => (
                <Tab
                  key={tab}
                  eventKey={tab}
                  title={
                    {
                      generales: "Datos generales",
                      precios: "Precios e impuestos",
                      costos: "Costos",
                      abasto: "Abasto",
                    }[tab]
                  }
                >
                  <Row className="g-3">
                    {tab === "generales" && fields.slice(0, 4).map(renderField)}
                    {tab === "generales" &&
                      catalogs.map((args) => remote(...args))}
                    {tab === "generales" && <Col md={8}><SatProductSelect value={form.clave_producto_servicio_sat} onChange={value => change("clave_producto_servicio_sat", value)} disabled={readOnly || saving} /></Col>}
                    {tab === "precios" &&
                      remote(
                        "id_moneda_venta",
                        "Moneda de venta",
                        "monedas",
                        "descripcion",
                        true,
                      )}
                    {tab === "costos" &&
                      remote(
                        "id_moneda_compra",
                        "Moneda de compra",
                        "monedas",
                        "descripcion",
                        true,
                      )}
                    {(tab === "generales" ? fields.slice(4) : fields).map(
                      renderField,
                    )}
                    {tab === "abasto" && (
                      <Field
                        name="tipo_control_auxiliar"
                        label="Control auxiliar"
                        value={form.tipo_control_auxiliar}
                        onChange={change}
                        disabled={readOnly || saving}
                        options={[
                          { value: "N", label: "Ninguno" },
                          { value: "L", label: "Lote" },
                          { value: "S", label: "Serie" },
                          { value: "F", label: "Fecha" },
                        ]}
                      />
                    )}
                    {tab === "abasto" &&
                      ["nivmov_frecuencia", "nivmov_promedio"].map((name) => (
                        <Field
                          key={name}
                          name={name}
                          label={
                            name === "nivmov_frecuencia"
                              ? "Nivel de movimiento por frecuencia"
                              : "Nivel de movimiento promedio"
                          }
                          value={form[name]}
                          onChange={change}
                          disabled={readOnly || saving}
                          options={["A", "B", "C", "D"].map((value) => ({
                            value,
                            label: value,
                          }))}
                        />
                      ))}
                  </Row>
                  <Row className="g-3 mt-3">
                    {(flags[tab] || [])
                      .filter(
                        ([name]) => typeof emptyProduct()[name] === "boolean",
                      )
                      .map(([name, label]) => (
                        <Field
                          key={name}
                          {...{ name, label }}
                          type="checkbox"
                          value={form[name]}
                          onChange={change}
                          disabled={readOnly || saving}
                          md={6}
                        />
                      ))}
                  </Row>
                </Tab>
              ))}
              <Tab eventKey="relaciones" title="Categorías y relaciones">
                <ProductRelations {...{ form, change, readOnly, saving }} />
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={saving}
          >
            Cerrar
          </Button>
          {!readOnly && (
            <Button type="submit" disabled={loading || saving || !!loadError}>
              {saving ? <Spinner size="sm" /> : <Save size={16} />} Guardar
              producto
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
function ProductRelations({ form, change, readOnly, saving }) {
  const sections = [
    [
      "categorias",
      "Categorías",
      "prod-categorias",
      "id_prod_categoria",
      [],
      "nombre",
    ],
    [
      "empresas",
      "Empresas",
      "empresas",
      "id_empresa",
      [
        ["minimo", "Mínimo"],
        ["maximo", "Máximo"],
        ["punto_reorden", "Punto de reorden"],
      ],
      "nombre",
    ],
    [
      "preciosgrupos",
      "Precios por grupo de cliente",
      "clientes-grupos",
      "id_cliente_grupo",
      [["monto", "Monto"]],
      "descripcion",
    ],
    [
      "prod_contenidos",
      "Productos contenidos",
      "productos",
      "id_producto",
      [["cantidad", "Cantidad"]],
      "descripcion",
    ],
    [
      "proveedores",
      "Proveedores",
      "proveedores",
      "id_proveedor",
      [
        ["precio", "Precio"],
        ["dias_entrega", "Días de entrega"],
      ],
      "nombre",
    ],
  ];
  return sections.map(
    ([name, label, endpoint, key, fields, searchProperty]) => (
      <section className="mb-4" key={name}>
        <h6>{label}</h6>
        {form[name].map((row, index) => {
          const update = (field, value) =>
            change(
              name,
              form[name].map((item, i) =>
                i === index ? { ...item, [field]: value } : item,
              ),
            );
          return (
            <Card key={index} className="mb-2">
              <Card.Body>
                <Row className="g-3">
                  <Col md={5}>
                    <RemoteSelect
                      name={key}
                      label="Selecciona"
                      value={row[key]}
                      disabled={readOnly || saving}
                      onChange={update}
                      loadOptions={(params) =>
                        listarCatalogo(endpoint, { ...params, searchProperty })
                      }
                      loadById={(id) => obtenerCatalogo(endpoint, id)}
                      getOptionLabel={labels}
                    />
                  </Col>
                  {fields.map(([field, fieldLabel]) => (
                    <Field
                      key={field}
                      name={field}
                      label={fieldLabel}
                      type="number"
                      value={row[field]}
                      onChange={update}
                      disabled={readOnly || saving}
                    />
                  ))}
                  {name === "proveedores" && (
                    <Col md={4}>
                      <RemoteSelect
                        name="id_moneda_compra"
                        label="Moneda de compra"
                        value={row.id_moneda_compra}
                        disabled={readOnly || saving}
                        onChange={update}
                        loadOptions={(params) =>
                          listarCatalogo("monedas", {
                            ...params,
                            searchProperty: "descripcion",
                          })
                        }
                        loadById={(id) => obtenerCatalogo("monedas", id)}
                        getOptionLabel={labels}
                      />
                    </Col>
                  )}
                </Row>
                {!readOnly && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-3"
                    onClick={() =>
                      change(
                        name,
                        form[name].filter((_, i) => i !== index),
                      )
                    }
                  >
                    <Trash2 size={16} /> Quitar
                  </Button>
                )}
              </Card.Body>
            </Card>
          );
        })}
        {!readOnly && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              change(name, [
                ...form[name],
                {
                  [key]: "",
                  ...Object.fromEntries(fields.map(([field]) => [field, 0])),
                },
              ])
            }
          >
            <Plus size={16} /> Agregar
          </Button>
        )}
      </section>
    ),
  );
}
